import { GmailAccessModeDialog, type GmailAccessMode } from "@/features/connectors/components/GmailAccessModeDialog";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useReducedMotion } from "framer-motion";
import * as React from "react";

import { useFileUpload } from "@/features/workspace/hooks/useFileUpload";
import {
  type CapabilityName,
  type ConnectionSummary,
  asDriveProvider,
  startCapabilityFlow,
} from "@ando/core";
import { localize, toast } from "@ando/ui";

import { useCapability } from "@/features/capabilities";
import {
  connectorsListKey,
  isLocalCredentialsCapability,
  useCapabilityConnections,
  useDisconnectConnection,
  useReauthConnection,
  useSubmitCredentials,
  visibleConnectorCatalog,
} from "@/features/connectors";
import { useConnectorEntitlements } from "@/features/connectors/hooks/useConnectorEntitlements";
import type { StartFlowField, StartFlowResult } from "@/features/connectors/components";
import { WhatsAppConnectorPanel } from "@/features/connectors/components/WhatsAppConnectorPanel";
import { useImportSpreadsheet } from "@/features/connectors/hooks/useImportSpreadsheet";
import { useSessionUserId } from "@/features/connectors/hooks/useSessionUserId";
import { useTestConnection } from "@/features/connectors/hooks/useTestConnection";
import {
  BenefitsGrid,
  CapabilityHeader,
  ConnectProviderCards,
  ConnectedAccountsSection,
  ConnectionsListSkeleton,
  ConnectorDetailSection,
  ConnectorDialogsLayer,
  DbErpManagementPanels,
  ErrorState,
  IntegrationsEmptyState,
  SyncSheetsLayer,
  asSpreadsheetSourceMode,
  computeStatus,
  humanizeError,
  metadataToInitialFields,
  resolveOAuthRedirect,
} from "@/features/integrations/parts";
import { useFilePicker } from "@/features/sync";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

/**
 * CapabilityDetailPage — per-capability route. May 24, 2026 rewrite.
 *
 * Replaces the previous inline-wizard mount that confused users by
 * stacking a "Provider → Autorizza" overlay on top of the page chrome
 * (no list of connected accounts, no benefits, "Vendor mancante per
 * capability X" banners surfacing as scary errors).
 *
 * Layout (V1 EnterpriseConnectorPage parity):
 *
 *   1. Breadcrumb ←  "Tutte le integrazioni"
 *   2. Header hero: badge + title + subtitle + status pill (no add-CTA —
 *      connecting happens via the always-visible provider cards below)
 *   3. Section "Account collegati" — ``<ConnectedAccountRow>`` per
 *      ConnectionSummary, with Gestisci/Riconnetti + kebab actions
 *   4. "Aggiungi un altro account" / empty-state — the shared
 *      ``<ConnectProviderCards>`` surface: connectable providers are ALWAYS
 *      visible (never hidden behind a dropdown), the SAME component whether
 *      you have zero or N accounts. KISS + DRY across every capability page.
 *   5. ``<ConnectAccountDialog>`` — Radix Dialog, opens AFTER the user
 *      picks a provider card. NEVER mounts inline.
 *
 * Flow rules:
 *   - Vendor is ALWAYS pre-selected via ConnectProviderCards BEFORE the
 *     dialog opens → ``vendorOf()`` cannot throw.
 *   - OAuth callback (``?status=connected``) on the parent ``index.tsx``
 *     route invalidates the cache → returning to this page shows the
 *     fresh row.
 *   - Disconnect / reauth are TanStack mutations with optimistic toast
 *     feedback + automatic list refresh.
 */
export const Route = createFileRoute("/_authed/_modal/integrations/$capability")({
  // ``?detail=<conn_id>`` makes the DB/ERP detail a URL-addressable dedicated
  // section (back/forward + refresh work; no modal stacking). ``detail`` is
  // OPTIONAL — the explicit return type keeps every existing ``<Link
  // to="/integrations/$capability">`` / ``navigate`` call valid without a search.
  validateSearch: (search: Record<string, unknown>): { detail?: string } => ({
    detail: typeof search.detail === "string" ? search.detail : undefined,
  }),
  loader: ({ params }) => {
    // Use the runtime-visible catalog so a desktop-only connector (filesystem)
    // 404s on the web build even via a direct URL — not just hidden from the
    // index grid.
    const entry = visibleConnectorCatalog.find((e) => e.slug === params.capability);
    if (!entry) throw notFound();
    return { entry };
  },
  component: CapabilityDetailPage,
});

function CapabilityDetailPage() {
  const { i18n } = useLingui();
  const { entry } = Route.useLoaderData();
  const spec = entry.spec;
  const search = Route.useSearch();
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();
  const entitlements = useConnectorEntitlements();
  const isEntitled = entitlements.canUse(spec.capability);
  const { state: capabilityState, diff } = useCapability(spec.capability);

  React.useEffect(() => {
    if (entitlements.isSuccess && !isEntitled) {
      void navigate({ to: "/integrations", replace: true });
    }
  }, [entitlements.isSuccess, isEntitled, navigate]);

  const { connections, isLoading, isError, refetch } = useCapabilityConnections(spec.capability, spec.capability === "email" ? "send" : undefined);

  const disconnectMutation = useDisconnectConnection();
  const reauthMutation = useReauthConnection();
  const submitCredentialsMutation = useSubmitCredentials();
  const importSpreadsheetMutation = useImportSpreadsheet();
  const testConnectionMutation = useTestConnection();

  // ── DB / ERP + spreadsheet post-create operator surfaces (Jul 2026) ──
  // "Gestisci →" on a DB/ERP/spreadsheet row navigates to the dedicated detail
  // SECTION ``<ConnectorDetailSection>`` (manifest-driven tabs: DB/ERP get
  // Schema · Contesto · Esempi + verify/discover/edit header actions; a
  // spreadsheet gets Schema only, no credential chrome), URL-addressable via
  // ``?detail=``. The DiscoveryDialog + EditConnectorDialog modals are launched
  // from the section header (DB/ERP only).
  const isDbErpRoute = spec.capability === "database" || spec.capability === "erp";
  // DB/ERP and spreadsheet share the same dedicated detail section — the BE
  // materialises every uploaded workbook as a queryable connector with one
  // ``table`` per sheet, so its Schema tab reuses the same ``SchemaViewer``.
  const isDbErpOrSpreadsheetRoute = isDbErpRoute || spec.capability === "spreadsheet";
  const [discoveringConnId, setDiscoveringConnId] = React.useState<string | null>(null);
  const [editingConnId, setEditingConnId] = React.useState<string | null>(null);
  // DB/ERP detail is a URL-addressable dedicated section (``?detail=<conn_id>``),
  // not a modal/drawer — deep destination work (schema + live data) reads as a
  // place you navigate to, back/forward + refresh work, no modal stacking.
  const detailConnId = search.detail ?? null;
  const openDetail = React.useCallback(
    (connId: string) => {
      void navigate({ to: ".", search: (prev) => ({ ...prev, detail: connId }) });
    },
    [navigate],
  );
  const closeDetail = React.useCallback(() => {
    void navigate({ to: ".", search: (prev) => ({ ...prev, detail: undefined }) });
  }, [navigate]);

  const discoveringConnection = React.useMemo(
    () => connections.find((c) => c.conn_id === discoveringConnId) ?? null,
    [connections, discoveringConnId],
  );
  const editingConnection = React.useMemo(
    () => connections.find((c) => c.conn_id === editingConnId) ?? null,
    [connections, editingConnId],
  );
  const detailConnection = React.useMemo(
    () => connections.find((c) => c.conn_id === detailConnId) ?? null,
    [connections, detailConnId],
  );

  const handleTestFromMenu = React.useCallback(
    (connId: string) => {
      testConnectionMutation.mutate(
        { connId },
        {
          onSuccess: (data) => {
            if (data.success) {
              toast.success(
                data.server_version
                  ? i18n._(msg`Connessione OK · ${data.latency_ms}ms · ${data.server_version}`)
                  : i18n._(msg`Connessione OK · ${data.latency_ms}ms`),
              );
            } else {
              toast.error(
                i18n._(msg`Verifica fallita: ${data.error ?? i18n._(msg`errore sconosciuto`)}`),
              );
            }
          },
          onError: (err) => {
            toast.error(
              i18n._(
                msg`Verifica non riuscita: ${err instanceof Error ? err.message : i18n._(msg`errore`)}`,
              ),
            );
          },
        },
      );
    },
    [testConnectionMutation, i18n],
  );

  // ── Drive provider pre-condition (spreadsheet capability only) ────────
  // The spreadsheet cloud source-modes (``google_drive`` / ``onedrive``)
  // borrow the user's existing Drive OAuth token to download the file —
  // they MUST find at least one matching ``drive`` connection. We always
  // mount the ``drive`` query here (hooks must be unconditional) but read
  // the result only when ``spec.capability === "spreadsheet"``. TanStack
  // dedups the request against the hub's existing ``drive`` cache when
  // present, so the extra round-trip is at worst a single warm-cache hit.
  const isSpreadsheetRoute = spec.capability === "spreadsheet";
  // Filesystem is the DESKTOP-ONLY connector — it uses its own custom dialog
  // (native folder picker → scan preview → ACL → fsSync) instead of the
  // generic OAuth/credentials flow.
  const isFilesystemRoute = spec.capability === "filesystem";
  // CAD library (Jun 13, 2026) shares the same "file" mental model as
  // spreadsheet: upload from disk OR pick from a cloud folder. The
  // cloud-precondition + filesystem fallback wiring is identical, so
  // we expand the existing drive-connection gate to cover both routes
  // via a shared ``isFileLibraryRoute`` flag.
  const isCadLibraryRoute = spec.capability === "cad_library";
  const isFileLibraryRoute = isSpreadsheetRoute || isCadLibraryRoute;
  const driveConnectionsQuery = useCapabilityConnections("drive");
  // The public provider rides ``provider`` (BE) with ``vendor`` as the
  // enterprise fallback — never the raw ``vendor`` alone (undefined for
  // white-label drive rows, which gated the cloud picker off).
  const hasGoogleDriveConnection =
    isFileLibraryRoute &&
    driveConnectionsQuery.connections.some((c) => (c.provider ?? c.vendor) === "google_drive");
  const hasOneDriveConnection =
    isFileLibraryRoute &&
    driveConnectionsQuery.connections.some((c) => (c.provider ?? c.vendor) === "onedrive");

  // Native file picker — wired to SpreadsheetConnectDialog so cloud source
  // modes get the Google Picker / OneDrive browser instead of paste-file-id.
  // Hook always mounts (must be unconditional); ``modalElement`` is rendered
  // at section bottom and is ``null`` whenever no picker is active.
  const filePicker = useFilePicker();

  // CAD library local upload (Jun 16, 2026). Replaces the prior
  // ``navigate({ to: "/workspace/conversations" })`` redirect that
  // forced the user out of the Tecnico hub just to drop a file. The
  // hidden ``<input type="file">`` is triggered when the user picks
  // the ``upload`` vendor card; ``useFileUpload`` then streams each
  // file to ``POST /api/v1/uploads`` with ``trigger_ingestion=true``
  // so the BE routes the bytes through the same CAD analyser that
  // already powers the chat-upload + Drive-sync paths (DRY: one
  // ingestion entry-point for every CAD source).
  //
  // ``accept`` mirrors ``CAD_ALL_EXTENSIONS`` (BE
  // ``src/constants/mime_types.py``) — keep it in sync if a new CAD
  // format is added (e.g. ``.brep``).
  const cadUploadInputRef = React.useRef<HTMLInputElement | null>(null);
  const cadUploader = useFileUpload();
  const queryClient = useQueryClient();
  const triggerCadUpload = React.useCallback(() => {
    cadUploadInputRef.current?.click();
  }, []);
  const handleCadFilesPicked = React.useCallback(
    async (ev: React.ChangeEvent<HTMLInputElement>) => {
      const input = ev.currentTarget;
      const picked = Array.from(input.files ?? []);
      // Reset the input element so picking the SAME file twice in a
      // row still fires ``change`` — browsers gate that on a new value.
      input.value = "";
      if (picked.length === 0) return;
      try {
        const jobs = await cadUploader.upload(picked, {
          triggerIngestion: true,
        });
        const done = jobs.filter((j) => j.status === "done").length;
        const failed = jobs.filter((j) => j.status === "error").length;
        if (done > 0) {
          // Toast with action button instead of auto-navigate — the
          // user is on the capability hub for a reason (might want to
          // upload more files, configure a Drive folder, etc.), and a
          // forced redirect would interrupt that flow. The "Apri
          // Tecnico" action is the Linear/GitHub pattern: clear
          // success signal + a single-click affordance to inspect
          // results. Jun 16, 2026 — favoured over auto-navigate per
          // user feedback.
          toast.success(i18n._(msg`${done} file CAD caricati. Indicizzazione in corso.`), {
            action: {
              label: i18n._(msg`Apri Tecnico`),
              onClick: () => {
                void navigate({ to: "/workspace/technical" });
              },
            },
          });
          // Invalidate the Tecnico library list so the new rows show
          // up without a manual refresh. Key prefix matches
          // ``dxfFilesQueryKey`` ([useDxfFiles]).
          void queryClient.invalidateQueries({ queryKey: ["dxf", "files"] });
        }
        if (failed > 0) {
          const first = jobs.find((j) => j.status === "error");
          toast.error(
            i18n._(msg`${failed} file CAD non caricati${first?.error ? `: ${first.error}` : ""}`),
          );
        }
      } catch (uploadErr) {
        toast.error(i18n._(msg`Errore upload CAD: ${humanizeError(uploadErr)}`));
      }
    },
    [cadUploader, i18n, navigate, queryClient],
  );

  // ── Dialog open + selected vendor state ───────────────────────────────
  // The dialog is gated entirely on ``selectedVendor`` being non-null.
  // AddAccountMenu sets it; closing the dialog clears it. This keeps
  // the dialog stateless about which vendor it represents.
  const [selectedVendor, setSelectedVendor] = React.useState<string | null>(null);
  const [gmailModeOpen, setGmailModeOpen] = React.useState(false);
  // Credentials schema pre-fetched by ``startConnect`` (below) — lets the
  // ConnectAccountDialog open straight on its form. Cleared on dialog close.
  const [credentialsFields, setCredentialsFields] = React.useState<
    readonly StartFlowField[] | null
  >(null);

  // ── WhatsApp inline panel (messaging capability only) ────────────────
  // Embedded Signup is popup-driven, not OAuth-redirect, so it does NOT
  // fit ConnectAccountDialog. The dedicated panel is rendered inline below
  // the connections list and self-manages connect/disconnect/mode detection.
  //
  // Mount policy (June 6, 2026): only render when WhatsApp is already
  // connected OR the user explicitly clicked "WhatsApp" in AddAccountMenu.
  // Always-on rendering polluted the page with the verbose disconnected-
  // state CTA + the "Tip: for Coexistence…" hint for every user who hadn't
  // shown interest yet, and made the AddAccountMenu pick visually inert
  // (the panel was already mounted, so scrollIntoView was a no-op).
  const whatsappPanelRef = React.useRef<HTMLDivElement | null>(null);
  const sessionUserId = useSessionUserId();
  const messagingHasWhatsApp =
    spec.capability === "messaging" &&
    (visibleConnectorCatalog
      .find((e) => e.slug === "messaging")
      ?.spec.vendors.some((v) => v.id === "whatsapp") ??
      false);
  // True once a whatsapp ConnectionSummary lands in the messaging list
  // (BE-persisted onboarding completed at any time, including a previous
  // session). The panel ALWAYS mounts when this is true so the user can
  // see status / disconnect.
  const whatsappConnected = React.useMemo(
    () => spec.capability === "messaging" && connections.some((c) => c.provider === "whatsapp"),
    [spec.capability, connections],
  );
  // User-intent flag: flips true on AddAccountMenu pick "whatsapp". The
  // panel mounts and the user sees the Embedded Signup CTA. Reset via
  // "Disconnect" inside the panel (handled by the panel itself when it
  // transitions to the connected state — the connectionSummary flag
  // takes over).
  const [whatsappRequested, setWhatsappRequested] = React.useState(false);
  const showWhatsappPanel = messagingHasWhatsApp && (whatsappConnected || whatsappRequested);

  // Auto-scroll + focus when the panel becomes visible (after a user pick).
  React.useEffect(() => {
    if (!showWhatsappPanel || !whatsappRequested) return;
    // The DOM mounts on the same tick the state flips; defer one frame so
    // the ref is populated before we scroll.
    const id = window.requestAnimationFrame(() => {
      whatsappPanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
      whatsappPanelRef.current?.querySelector<HTMLElement>("h2")?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [showWhatsappPanel, whatsappRequested]);

  // ``handlePickVendor`` + ``startConnect`` are defined below, after
  // ``handleStartFlow`` (they call it — useCallback dep-order matters).

  // ── OAuth-start handler — fires ``POST /connectors/{cap}/start`` ──────
  // and routes the discriminated response into the dialog state machine.
  // No throws: errors surface as ``{type:"error"}`` so the dialog can
  // render an inline banner with retry, NEVER as a page-level banner.
  const handleStartFlow = React.useCallback(
    async (vendorId: string, accessMode?: GmailAccessMode): Promise<StartFlowResult> => {
      // Guard: missing vendor is now a UX error, not a BE 4xx — we
      // surface it actionable in the dialog instead of letting the BE
      // reject with a generic message. ``vendorOf`` no longer throws
      // (May 24 fix) so we have to check at the call site.
      if (!vendorId) {
        return {
          type: "error",
          message: i18n._(msg`Seleziona prima un provider dal menu.`),
        };
      }
      // WhatsApp Embedded Signup never reaches this handler: every vendor
      // pick funnel (``handlePickVendor``) intercepts it upstream and
      // scrolls to the inline ``<WhatsAppConnectorPanel>`` instead of
      // opening ConnectAccountDialog. If we ever get a ``vendorId ===
      // "whatsapp"`` here it's a code-path drift — log + bail cleanly.
      if (spec.capability === "messaging" && vendorId === "whatsapp") {
        console.warn(
          "[messaging] handleStartFlow reached with vendor=whatsapp — " +
            "should have been intercepted by handlePickVendor",
        );
        return { type: "error", message: "" };
      }
      // Local-credentials capabilities (``database``, ``erp``) live on
      // the legacy ``/api/v1/enterprise/connectors`` surface, NOT the
      // unified ``/connectors/{capability}/start`` router. Hitting the
      // unified ``/start`` for them returns ``Unknown capability:
      // <slug>`` because they're not in the BE
      // ``SUPPORTED_CAPABILITIES`` whitelist (by design —
      // credentials_form is the only flow they ship).
      //
      // The spec already declares the per-vendor credentials step
      // (``hostPortFields`` / ``sqliteFields`` / ``odbcFields``), so we
      // short-circuit the BE round-trip and jump straight to the form.
      // ``handleSubmitCredentials`` already dispatches to
      // ``/enterprise/connectors`` via ``submitConnectionForCapability``,
      // so the form submit lands on the right endpoint with zero
      // changes. We deliberately scope this to the narrow set rather
      // than ``isEnterpriseCapability`` (which would erroneously
      // short-circuit OAuth-based ``crm`` / ``accounting`` /
      // ``open_banking`` that are members for picker-scope reasons but
      // DO need the unified router to launch OAuth).
      if (isLocalCredentialsCapability(spec.capability)) {
        return { type: "credentials_form" };
      }
      try {
        const flow = await startCapabilityFlow(spec.capability as CapabilityName, {
          vendor: vendorId,
          ...(spec.capability === "email" && vendorId === "gmail" && accessMode ? { access_mode: accessMode } : {}),
        });
        if (flow.flow_type === "oauth_redirect") {
          // Resolve + validate the URL: BE may return a relative path
          // which would break in Tauri (origin = tauri://localhost
          // with no proxy). Pre-resolve against ``apiClient.getBaseUrl()``
          // and reject non-http(s) schemes (open-redirect / XSS).
          try {
            const safe = resolveOAuthRedirect(flow.oauth_url);
            window.location.href = safe;
            return { type: "oauth_redirect", oauth_url: safe };
          } catch (e) {
            return {
              type: "error",
              message: e instanceof Error ? e.message : i18n._(msg`URL non valido`),
            };
          }
        }
        if (flow.flow_type === "credentials_form") {
          // Forward the BE schema to the dialog so specs without a
          // static ``kind:"credentials"`` step (open_banking — consent
          // is OAuth-shaped) still render the prompted inputs. The BE
          // ships ``key/label/type/required/placeholder`` per field;
          // the dialog expects ``name`` (matches the static spec
          // shape) — adapt at the boundary.
          const fields = (flow.form_schema?.fields ?? []).map((f) => ({
            name: f.key,
            label: f.label,
            type: f.type,
            required: f.required,
            placeholder: f.placeholder,
            // ``helperText`` is the BE-side guidance rendered under the
            // input — used by PSD2 OB to explain that the field accepts
            // both P.IVA and Codice Fiscale.
            helperText: (f as { helperText?: string }).helperText,
          }));
          return { type: "credentials_form", fields };
        }
        return {
          type: "error",
          message: i18n._(
            msg`Tipo di flusso non supportato (${flow.flow_type as string}). Aggiorna l'app.`,
          ),
        };
      } catch (err) {
        return {
          type: "error",
          message: humanizeError(err),
        };
      }
    },
    [spec.capability, i18n],
  );

  // ── Connect dispatch — no confirm modal (KISS) ───────────────────────
  // Clicking a provider card starts the flow immediately: OAuth redirects to
  // the provider with ZERO intermediate modal; a credentials flow opens
  // ConnectAccountDialog straight on its form. The old "Autorizza" confirm
  // step (which re-listed the benefits already on the page) is gone.
  const startConnect = React.useCallback(
    async (vendorId: string) => {
      if (spec.capability === "email" && vendorId === "gmail") { setGmailModeOpen(true); return; }
      const result = await handleStartFlow(vendorId);
      if (result.type === "oauth_redirect") return; // handleStartFlow already navigated
      if (result.type === "credentials_form") {
        setCredentialsFields(result.fields ?? null);
        setSelectedVendor(vendorId); // opens the dialog straight on its form
        return;
      }
      toast.error(result.message);
    },
    [handleStartFlow, spec.capability],
  );

  /**
   * Vendor-pick funnel — every "connect a provider" entry point (empty-state
   * cards, "Aggiungi un altro account") goes through here. WhatsApp → inline
   * panel; CAD → upload / umbrella-source redirect; spreadsheet / filesystem →
   * their dedicated dialogs; everything else → ``startConnect`` (no confirm modal).
   */
  const handlePickVendor = React.useCallback(
    (vendorId: string) => {
      if (spec.capability === "messaging" && vendorId === "whatsapp") {
        setWhatsappRequested(true);
        return;
      }
      if (isCadLibraryRoute && vendorId === "upload") {
        triggerCadUpload();
        return;
      }
      if (isCadLibraryRoute && (vendorId === "google_drive" || vendorId === "onedrive")) {
        void navigate({ to: "/integrations/$capability", params: { capability: "drive" } });
        return;
      }
      if (isCadLibraryRoute && vendorId === "filesystem") {
        void navigate({ to: "/integrations/$capability", params: { capability: "filesystem" } });
        return;
      }
      // Spreadsheet / filesystem mount their own dedicated dialogs.
      if (isSpreadsheetRoute || isFilesystemRoute) {
        setSelectedVendor(vendorId);
        return;
      }
      // OAuth / credentials → straight into the flow, no confirm modal.
      void startConnect(vendorId);
    },
    [
      spec.capability,
      isCadLibraryRoute,
      isSpreadsheetRoute,
      isFilesystemRoute,
      navigate,
      triggerCadUpload,
      startConnect,
    ],
  );

  // ── Credentials submit — POST /connectors/{cap}/credentials ──────────
  //
  // For non-OAuth vendors (Aruba PEC, NAS, DXF, VerifyID, Acube,
  // InfoCert IMAP, Aruba Conservazione). The BE encrypts secrets at
  // rest via envelope-encryption (same pipeline OAuth tokens use); the
  // saved connection becomes indistinguishable from an OAuth one — so
  // list / disconnect / reauth all keep working unchanged.
  //
  // The mutation ``onSuccess`` already invalidates the per-capability
  // list. We close the dialog + show a success toast here; the page
  // re-renders the connected accounts section with the new row.
  // On error we re-throw so the dialog stays in ``form`` state with the
  // inline error visible (the user can fix the field + retry).
  const handleSubmitCredentials = React.useCallback(
    async (vendor: string, values: Record<string, string>): Promise<void> => {
      try {
        const result = await submitCredentialsMutation.mutateAsync({
          capability: spec.capability,
          vendor,
          fields: values,
        });
        // ── Two-stage flow (Open Banking PSD2) ─────────────────────────
        // The BE may attach an ``oauth_url`` follow-up when the
        // credentials submit is only stage 1 (form captured the VAT)
        // and stage 2 needs the user to authorise the consent at the
        // bank. Same wire shape ``start_flow`` returns for OAuth, so
        // the SPA reuses the existing ``window.location.href`` hand-off.
        if (result && typeof result === "object" && result.oauth_url) {
          window.location.href = result.oauth_url;
          return;
        }
        toast.success(i18n._(msg`${localize(spec.title, i18n)} collegata`));
        setSelectedVendor(null);
      } catch (err) {
        // Re-throw so the dialog stays open with the inline error —
        // a toast alone disappears, the user wouldn't know what to fix.
        throw new Error(humanizeError(err));
      }
    },
    [submitCredentialsMutation, spec.capability, spec.title, i18n],
  );

  // ── Reauth — fire BE call, redirect on oauth_redirect ─────────────────
  const handleReauth = React.useCallback(
    (connectionId: string, vendor: string) => {
      reauthMutation.mutate(
        { capability: spec.capability, vendor, connectionId, ...(connections.some((c) => c.conn_id === connectionId && (c.access_mode === "send_only" || c.connection_type === "email_send")) ? { access_mode: "send_only" as const } : {}) },
        {
          onSuccess: (flow) => {
            if (flow.flow_type === "oauth_redirect") {
              try {
                window.location.href = resolveOAuthRedirect(flow.oauth_url);
              } catch (e) {
                toast.error(humanizeError(e));
              }
            } else if (flow.flow_type === "credentials_form") {
              setCredentialsFields(
                (flow.form_schema?.fields ?? []).map((field) => ({
                  name: field.key,
                  label: field.label,
                  type: field.type,
                  required: field.required,
                  placeholder: field.placeholder,
                  helperText: (field as { helperText?: string }).helperText,
                })),
              );
              setSelectedVendor(vendor);
            } else {
              toast.error(i18n._(msg`Questo provider non supporta la riconnessione.`));
            }
          },
          onError: (err) => {
            toast.error(i18n._(msg`Riconnessione fallita: ${humanizeError(err)}`));
          },
        },
      );
    },
    [reauthMutation, refetch, spec.capability, i18n, connections],
  );

  // ── Disconnect — confirm-less for now (kebab → instant); a future
  //    confirm dialog can wrap this with ``window.confirm()`` or a
  //    Radix AlertDialog if telemetry shows accidental disconnects.
  const handleDisconnect = React.useCallback(
    (connectionId: string, vendor?: string) => {
      disconnectMutation.mutate(
        { capability: spec.capability, connectionId, vendor },
        {
          onSuccess: () => {
            toast.success(i18n._(msg`Account disconnesso`));
          },
          onError: (err) => {
            toast.error(
              i18n._(
                msg`Disconnessione fallita: ${err instanceof Error ? err.message : i18n._(msg`errore sconosciuto`)}`,
              ),
            );
          },
        },
      );
    },
    [disconnectMutation, spec.capability, i18n],
  );

  // ── Sync flow ────────────────────────────────────────────────────────
  // Per-capability sync UX:
  //   - ``email``      → ``<EmailSyncFiltersSheet>`` (filters + presets)
  //   - ``messaging``  → ``<ChatSyncFiltersSheet>`` (provider-conditional picker)
  //   - ``drive``      → TODO: ``<DocSyncManager>`` (with native Picker dispatch)
  //   - other          → fallback toast (no UI yet)
  const [emailSyncTarget, setEmailSyncTarget] = React.useState<ConnectionSummary | null>(null);
  const [chatSyncTarget, setChatSyncTarget] = React.useState<ConnectionSummary | null>(null);
  const [docsSyncTarget, setDocsSyncTarget] = React.useState<ConnectionSummary | null>(null);

  /**
   * Open the management UI for this connection — single entry point for
   * every sync interaction. The ``Gestisci →`` button on the row maps
   * here directly; the kebab no longer exposes a sync shortcut.
   *
   * Per-capability dispatch is encoded in a declarative ``MANAGE_HANDLERS``
   * lookup (see below the component). Adding a new capability = one entry
   * in that map, no new ``if`` branch.
   */
  const manageDeps = React.useMemo<ManageDeps>(
    () => ({
      i18n,
      refetch,
      setEmailSyncTarget,
      setChatSyncTarget,
      setDocsSyncTarget,
      setEditingConnId,
      openDetail,
    }),
    [i18n, refetch, openDetail],
  );
  const handleManage = React.useCallback(
    (connection: ConnectionSummary) => {
      // The row only renders the "Gestisci" button when the manifest declares
      // a manage surface (``manage !== null``), and every such capability has a
      // handler here — so this is always a real dispatch. No dead-end fallback
      // toast: a connector with nothing to manage simply shows no button.
      MANAGE_HANDLERS[spec.capability]?.(connection, manageDeps);
    },
    [spec.capability, manageDeps],
  );

  // ── Capability gating (license + plan) ─────────────────────────────────
  const capabilityGranted = capabilityState === "satisfied";
  // Upgrade target — present only when the BE returned a provider + a
  // connection_type to route to; the header renders an "Upgrade" CTA only
  // when both are present (same gating as the inline version).
  const upgradeCapability =
    diff?.upgrade_paths?.[0]?.provider && diff.upgrade_paths[0]?.connection_type
      ? diff.upgrade_paths[0].connection_type
      : null;

  const hasConnections = connections.length > 0;
  const status = computeStatus(connections);

  // Dedicated full-width detail SECTION takeover (DB/ERP + spreadsheet) —
  // replaces the list with the manifest-driven schema/context/examples surface.
  // The discovery + edit modals (DB/ERP only) are launched from its header
  // actions, so they stay mounted alongside.
  if (isDbErpOrSpreadsheetRoute && detailConnection) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col px-8 pt-4">
        <ConnectorDetailSection
          connection={detailConnection}
          onBack={closeDetail}
          onDiscover={(connId) => setDiscoveringConnId(connId)}
          onEditCredentials={(c) => setEditingConnId(c.conn_id)}
          onTestConnection={(c) => handleTestFromMenu(c.conn_id)}
        />
        <DbErpManagementPanels
          spec={spec}
          discoveringConnection={discoveringConnection}
          editingConnection={editingConnection}
          editingInitialFields={
            editingConnection !== null ? metadataToInitialFields(editingConnection.metadata) : {}
          }
          onCloseDiscovery={() => setDiscoveringConnId(null)}
          onCloseEdit={() => setEditingConnId(null)}
        />
      </div>
    );
  }

  // A copied/deep link must obey the same entitlement matrix as the catalog.
  // Wait for a successful entitlement response before denying; transient
  // failures remain fail-open and the backend API guard stays authoritative.
  if (entitlements.isSuccess && !isEntitled) return null;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-8 pt-2">
      <CapabilityHeader
        entry={entry}
        spec={spec}
        status={status}
        connectionCount={connections.length}
        capabilityGranted={capabilityGranted}
        upgradeCapability={upgradeCapability}
      />

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      {isLoading ? (
        <ConnectionsListSkeleton />
      ) : isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : hasConnections ? (
        <ConnectedAccountsSection
          spec={spec}
          connections={connections}
          reducedMotion={!!reducedMotion}
          disconnectingId={
            disconnectMutation.isPending
              ? (disconnectMutation.variables?.connectionId ?? null)
              : null
          }
          reauthingId={
            reauthMutation.isPending ? (reauthMutation.variables?.connectionId ?? null) : null
          }
          onReauth={handleReauth}
          onDisconnect={handleDisconnect}
          onManage={handleManage}
        />
      ) : (
        <IntegrationsEmptyState
          spec={spec}
          capabilityGranted={capabilityGranted}
          onConnect={handlePickVendor}
        />
      )}

      {/* "Aggiungi un altro account" — the SAME provider-cards surface as the
          empty state, kept visible once connected so adding another provider
          (or a second account) never hides behind a dropdown. KISS + DRY:
          one connect surface, always visible, on every capability page. */}
      {!isLoading && !isError && hasConnections && capabilityGranted ? (
        <section className="flex flex-col gap-2">
          <header className="flex items-center gap-2">
            <h2 className="font-semibold text-[10.5px] text-ink-2 uppercase tracking-[0.08em]">
              {i18n._(msg`Aggiungi un altro account`)}
            </h2>
          </header>
          <ConnectProviderCards
            spec={spec}
            onConnect={handlePickVendor}
            className="xl:grid-cols-3"
          />
        </section>
      ) : null}

      {/* WhatsApp inline panel — messaging capability only. Gated mount
          (June 6 2026): renders only when the user has an existing
          WhatsApp connection OR has explicitly clicked "WhatsApp" in
          AddAccountMenu. Self-manages connect/disconnect lifecycle
          (Embedded Signup popup, mode detection, history-sync toggle,
          AlertDialog-gated disconnect). */}
      {showWhatsappPanel && sessionUserId ? (
        <section
          ref={whatsappPanelRef}
          aria-labelledby="wa-panel-heading"
          className="flex flex-col gap-2"
        >
          <header className="flex items-center gap-2">
            <h2
              id="wa-panel-heading"
              className="font-semibold text-ink-2 text-xs uppercase tracking-[0.08em]"
            >
              WhatsApp Business
            </h2>
          </header>
          <WhatsAppConnectorPanel userId={sessionUserId} />
        </section>
      ) : null}

      {/* Benefits grid — always visible below the list (V1 parity) */}
      {spec.benefits.length > 0 && hasConnections ? (
        <BenefitsGrid benefits={spec.benefits} />
      ) : null}

      {/* ── ConnectAccountDialog — Radix Dialog, opens only when a vendor is
          picked. The spreadsheet capability uses a dedicated dialog (file
          upload + sheet picker + cloud-provider gating); the layer gates
          the two so the route never mounts both at once. ── */}
      <ConnectorDialogsLayer
        isSpreadsheet={isSpreadsheetRoute}
        isFilesystem={isFilesystemRoute}
        spec={spec}
        selectedVendor={selectedVendor}
        onClose={() => {
          setSelectedVendor(null);
          setCredentialsFields(null);
        }}
        connectInitialFields={credentialsFields}
        spreadsheetSourceMode={
          isSpreadsheetRoute && selectedVendor !== null
            ? asSpreadsheetSourceMode(selectedVendor)
            : null
        }
        providerConnected={
          selectedVendor === "upload"
            ? true
            : selectedVendor === "google_drive"
              ? hasGoogleDriveConnection
              : selectedVendor === "onedrive"
                ? hasOneDriveConnection
                : false
        }
        cloudConnections={driveConnectionsQuery.connections}
        openPicker={filePicker.open}
        onNavigateToDriveSetup={() => {
          // Drive setup lives on the canonical ``drive`` capability page;
          // every cloud provider (Google Drive / OneDrive / SharePoint /
          // Notion) is added from that single hub. The user picks the
          // provider AFTER landing.
          setSelectedVendor(null);
          void navigate({
            to: "/integrations/$capability",
            params: { capability: "drive" },
          });
        }}
        onImportSpreadsheet={async (options) => {
          const result = await importSpreadsheetMutation.mutateAsync(options);
          return { connector_id: String(result.connector_id) };
        }}
        onImportSuccess={(_connectorId) => {
          // June 13 2026 — ingestion is async: ``/import-spreadsheet`` returns
          // 202 + workflow_id after staging the file to MinIO + starting
          // ``SpreadsheetIngestionWorkflow``. The connector row already
          // exists in ``pending`` state; the progress + terminal events
          // ride the existing ``ws_bcast:drive_sync`` channel
          // (``source="spreadsheet"``) and SyncContext flips the card to
          // ``healthy`` when the workflow completes. We don't open
          // SchemaViewer here anymore because the schema metadata isn't
          // populated yet — the connector card link does it on click once
          // the row is healthy.
          toast.success(
            i18n._(msg`Caricamento avviato — il connettore sarà pronto fra pochi minuti.`),
          );
          setSelectedVendor(null);
          void refetch();
        }}
        onFilesystemSuccess={(connectorId) => {
          // The FilesystemConnectDialog already toasts on the sync result
          // (file count); here we just close + route to the dashboard so
          // the user lands on the surface where the indexed folder shows up.
          setSelectedVendor(null);
          void navigate({
            to: "/workspace/dashboard",
            search: { openConnection: connectorId },
          });
        }}
        onBridgeRegistered={() => {
          // Enterprise DB bridge (desktop-only, database capability). The
          // wizard already persisted the row via POST /enterprise-connectors/
          // bridge and the DSN into the OS keychain (Rust `keyring`); we mirror the cloud
          // ``handleSubmitCredentials`` post-success behaviour — toast + close
          // the dialog + invalidate the per-capability list so the fresh
          // bridge row lands in "Account collegati" without a manual refresh.
          toast.success(i18n._(msg`${localize(spec.title, i18n)} collegata`));
          setSelectedVendor(null);
          void queryClient.invalidateQueries({
            queryKey: connectorsListKey(spec.capability),
          });
        }}
        onStartFlow={handleStartFlow}
        onSubmitCredentials={handleSubmitCredentials}
      />

      <GmailAccessModeDialog open={gmailModeOpen} onOpenChange={setGmailModeOpen} onContinue={async (mode) => {
        const result = await handleStartFlow("gmail", mode);
        if (result.type === "error") toast.error(result.message);
      }} />

      {/* ── Per-capability sync surfaces (email / chat / drive). The
          layer renders nothing when the capability doesn't match its
          three sheets or when no target is set — same gating the inline
          version applied. ── */}
      <SyncSheetsLayer
        capability={spec.capability}
        emailSyncTarget={emailSyncTarget}
        chatSyncTarget={chatSyncTarget}
        docsSyncTarget={docsSyncTarget}
        onCloseEmailSync={() => setEmailSyncTarget(null)}
        onCloseChatSync={() => setChatSyncTarget(null)}
        onCloseDocsSync={() => setDocsSyncTarget(null)}
      />

      {/* DB/ERP + spreadsheet discovery/edit modals live in the detail SECTION
          branch (above), launched from its header — not in the list view. */}

      {filePicker.modalElement}

      {/* Hidden CAD upload input — mounted unconditionally but only
          triggered via ``triggerCadUpload`` on the ``cad_library``
          route's "Carica file" vendor card. Accept list mirrors
          ``CAD_ALL_EXTENSIONS`` on the BE so the OS file dialog hides
          unsupported types up-front. */}
      {isCadLibraryRoute ? (
        <input
          ref={cadUploadInputRef}
          type="file"
          multiple
          accept=".dxf,.dwg,.step,.stp,.iges,.igs,.stl,.pdf"
          className="sr-only"
          aria-hidden
          tabIndex={-1}
          onChange={handleCadFilesPicked}
          data-testid="cad-library-upload-input"
        />
      ) : null}
    </div>
  );
}

// ── Per-capability "Gestisci" dispatch table ───────────────────────────
//
// One entry per capability that has a non-default "Gestisci →" behaviour.
// Unlisted capabilities fall back to the generic "in arrivo" toast inside
// ``handleManage``. Adding a new capability with custom behaviour = one
// entry here; no new ``if`` branch in the component body.

interface ManageDeps {
  readonly i18n: ReturnType<typeof useLingui>["i18n"];
  readonly refetch: () => unknown;
  readonly setEmailSyncTarget: (c: ConnectionSummary) => void;
  readonly setChatSyncTarget: (c: ConnectionSummary) => void;
  readonly setDocsSyncTarget: (c: ConnectionSummary) => void;
  readonly setEditingConnId: (id: string) => void;
  readonly openDetail: (id: string) => void;
}

type ManageHandler = (conn: ConnectionSummary, deps: ManageDeps) => void;

// Keyed by spec.capability (string) — wider than CapabilityName because
// spreadsheet / database / erp aren't in the BE-canonical capability union
// but exist as catalog entries with their own management flows.
const MANAGE_HANDLERS: Readonly<Record<string, ManageHandler>> = {
  email: (conn, { i18n, setEmailSyncTarget }) => {
    if (!conn.connection_key) {
      toast.error(i18n._(msg`Account email senza indirizzo — riconnetti l'account.`));
      return;
    }
    setEmailSyncTarget(conn);
  },
  // Aruba PEC + InfoCert Legalmail reuse the email filter sheet (same
  // date/label surface); the sheet layer maps them to provider
  // "aruba"/"infocert" for the /email-management/* calls.
  aruba_pec: (conn, { i18n, setEmailSyncTarget }) => {
    if (!conn.connection_key) {
      toast.error(i18n._(msg`Casella PEC senza indirizzo — riconnetti l'account.`));
      return;
    }
    setEmailSyncTarget(conn);
  },
  infocert_pec: (conn, { i18n, setEmailSyncTarget }) => {
    if (!conn.connection_key) {
      toast.error(i18n._(msg`Casella PEC senza indirizzo — riconnetti l'account.`));
      return;
    }
    setEmailSyncTarget(conn);
  },
  messaging: (conn, { setChatSyncTarget }) => {
    setChatSyncTarget(conn);
  },
  drive: (conn, { i18n, setDocsSyncTarget }) => {
    // The native picker dispatches on the provider; surface a friendly
    // message instead of a silent no-op if it can't be resolved.
    if (!asDriveProvider(conn.provider ?? conn.vendor)) {
      toast.error(i18n._(msg`Provider del drive non riconosciuto — riconnetti l'account.`));
      return;
    }
    setDocsSyncTarget(conn);
  },
  spreadsheet: (conn, { openDetail }) => {
    // Jul 2026 — "Gestisci" on a workbook row navigates to the dedicated detail
    // SECTION (``?detail=<conn_id>``): a Schema tab (sheets → tables → columns +
    // live samples), no credential chrome.
    openDetail(conn.conn_id);
  },
  database: (conn, { openDetail }) => {
    // Jul 2026 — "Gestisci" on a DB/ERP row navigates to the dedicated detail
    // SECTION (``?detail=<conn_id>``): Schema · Contesto · Esempi tabs +
    // verify/discover/edit header actions.
    openDetail(conn.conn_id);
  },
  erp: (conn, { openDetail }) => {
    openDetail(conn.conn_id);
  },
  filesystem: (conn, { i18n, refetch }) => {
    // Desktop-only re-sync: re-scan the folder + push new/changed files
    // through the SAME shared ``runFilesystemSync`` core. Dynamic import
    // keeps the Tauri/fsBridge code path out of the web bundle's eager
    // graph — it only loads when a filesystem connector triggers a
    // manage click.
    const meta = (conn.metadata ?? {}) as Record<string, unknown>;
    const roots = Array.isArray(meta.base_paths) ? (meta.base_paths as string[]) : [];
    if (roots.length === 0) {
      toast.error(i18n._(msg`Nessun percorso configurato per questa cartella.`));
      return;
    }
    void (async () => {
      toast.info(i18n._(msg`Sincronizzazione avviata…`));
      try {
        const { runFilesystemSync } = await import("@/features/connectors/filesystem-desktop");
        const summary = await runFilesystemSync({
          connectorId: conn.conn_id,
          roots,
        });
        if (summary.failedFiles > 0) {
          toast.error(i18n._(msg`${summary.failedFiles} file non caricati · riprova.`));
        } else if (summary.uploadedFiles > 0) {
          toast.success(i18n._(msg`${summary.uploadedFiles} file sincronizzati`));
        } else if (summary.matchedFiles === 0) {
          // Aug 26, 2026 — a zero-match scan is AMBIGUOUS on macOS: TCC can
          // silently deny the walker on gated folders (Downloads/Documents/
          // Desktop). Probe before blaming the folder's contents.
          const { FS_PERMISSION_MESSAGE, diagnoseEmptyScan } = await import(
            "@/features/connectors/filesystem-desktop"
          );
          const verdict = await diagnoseEmptyScan(roots);
          if (verdict === "permission_denied") {
            toast.error(i18n._(FS_PERMISSION_MESSAGE), { duration: 12000 });
          } else if (verdict === "empty") {
            toast.warning(i18n._(msg`La cartella è vuota.`));
          } else {
            toast.warning(i18n._(msg`Nessun file compatibile trovato (pdf, docx, xlsx…).`));
          }
        } else {
          toast.success(i18n._(msg`Già aggiornata · nessuna modifica`));
        }
        refetch();
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : i18n._(msg`Sincronizzazione non riuscita.`),
        );
      }
    })();
  },
};
